import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter, map, skip, switchMap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { Logger } from '@core';
import { I18nService } from '@app/i18n';
import { GlobalEventsService } from './global-events.service';
import { MatDialog } from '@angular/material/dialog';
import { TermsPopupComponent } from './terms-popup/terms-popup.component';
import {
  DrawerCommand,
  DrawerConfig,
  DrawerFacadeService,
} from './@core/services/drawer-facade.service';
import { MatDrawer } from '@angular/material/sidenav';
import { CdkPortalOutlet, PortalOutlet } from '@angular/cdk/portal';
import { AssetsPipe } from './@shared/assets/assets.pipe';
import { NetworkService } from './@core/services/network.service';
import { CoreService } from './@core/services/core.service';
import { AnalyticsService } from './ahoy-analytics/analytics.service';
import { getHtmlDirStorageKey } from './@core/services/language.service';

const log = new Logger('App');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('drawer', { static: true }) drawer!: MatDrawer;
  @ViewChild('drawerContent', { read: ViewContainerRef, static: true })
  drawerContent!: ViewContainerRef;
  termsDialogDisplayed = false;
  drawerConfig: DrawerConfig = {};
  dir: string;
  private destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService,
    private globalEventsService: GlobalEventsService,
    public dialog: MatDialog,
    private i18nService: I18nService,
    private drawerFacadeService: DrawerFacadeService,
    private assetPipe: AssetsPipe,
    private networkService: NetworkService,
    private coreService: CoreService,
    private analytics: AnalyticsService,
  ) {
    this.switchTheme(environment.theme);
    const link = document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'icon';
    link.href = assetPipe.transform('assets/flyplus/favicon.svg');

    document.getElementsByTagName('head')[0].appendChild(link);

    // do not remove the analytics injection, even if this subscription is removed
    // this initializes page tracking through the router
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        // delay to let title-setting logic (below) run first, so page_title is accurate
        setTimeout(() => {
          this.analytics.logPageView(event.urlAfterRedirects);
        }, 1000);
      });
  }
  ngAfterViewInit(): void {
    this.drawerFacadeService
      .setDrawer(this.drawer)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((config: DrawerConfig) => {
        this.drawerConfig = config || {};
      });
    this.drawerFacadeService.setViewContainerRef(this.drawerContent);
  }
  ngOnInit() {
    const splash = document.getElementById('splash-screen');
    if (splash) {
      splash.style.display = 'none';
    }
    this.networkService.isOnline$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((isOnline) => {
        if (!isOnline) {
          this.coreService.notify(
            'You are offline. Check your internet connection.',
          );
        } else {
          this.coreService.notify('You are back online!');
        }
      });
    this.dir = localStorage.getItem(getHtmlDirStorageKey());
    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    log.debug('init');

    this.titleService.setTitle(environment.name);

    // Setup translations
    this.i18nService.init(
      environment.defaultLanguage,
      environment.supportedLanguages,
    );

    const onNavigationEnd = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    );

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        const title = event.title;
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });

    this.globalEventsService.onPromotToAcceptTerms
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.showTermsDialog();
      });
  }

  switchTheme(clientName: string) {
    document.documentElement.setAttribute('data-theme', clientName);
  }

  showTermsDialog() {
    // if already handled --> return
    // terms & conditions most likely won't update more than once per application life cycle
    if (this.termsDialogDisplayed) {
      return;
    }
    this.termsDialogDisplayed = true;

    const dialogRef = this.dialog.open(TermsPopupComponent, {
      width: '750px',
      height: '750px',
      disableClose: true,
      data: {},
    });

    dialogRef.afterClosed().subscribe();
  }

  ngOnDestroy() {
    this.i18nService.destroy();
  }
}
